import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import groupBy from 'lodash/groupBy';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';
import {
  CohortDTOTypeEnum,
  SloGroupCohortSummaryDTO,
  SloGroupDTO,
} from '@atlassiansox/phobos-client-ts';
import { useQuery } from '@tanstack/react-query';
import { useQueryParams } from 'use-query-params';
import EmptyState from '@atlaskit/empty-state';

import {
  breakWord,
  extendWidth,
  flex,
  flexDirectionColumn,
  flexStart,
  spaceGapMinimum,
  spaceGapRelaxed,
  spaceTopCozy,
} from '@/styles';
import { StageDisplayContext, StageDisplayType } from '@/controllers/stage-display-controller';
import { mapParamsToAPIQuery } from '@/components/composed/catalog-search-bar/services/utils';
import { querySpec } from '@/services/api-service/api-queries';
import { useFetchErrorController, usePaginationController } from '@/controllers';
import { Params } from '@/components/composed/catalog-search-bar/types';
import { apiFetch } from '@/services';

import { getPagesFromTotalPage } from '../../search/utils';
import SearchPageSkeleton from '../../search/search-results/search-page-skeleton';
import { BASE_SLOS_PAGE_SIZE } from '../constants';

import FilterBar from './filter-bar';
import SLOCohort from './slo-cohort';
import { CohortType, cohortTypeSorted, sloGroupSearchObjectType } from './types';
import { baseSloCohortPlaceholderStyles, stickyHeadStyles } from './styles';

type SLOPanelProps = {
  data: SloGroupDTO;
};

const SLOPanel: FC<SLOPanelProps> = ({ data: sloGroupData }) => {
  const { publicId, name } = sloGroupData;

  const { stageDisplayType } = useContext(StageDisplayContext);
  const isFullScreen = stageDisplayType === StageDisplayType.FullScreen;

  const [, setQuery] = useQueryParams({});
  const { onError } = useFetchErrorController();
  const { currentPage, onPageChange, setCurrentPage } = usePaginationController();
  const { search } = useLocation();
  const {
    cohortType,
    hasBreachStatusOnly,
    hideDrafts,
    currentPageBase,
    searchWithoutPage,
    cohortParams,
  } = useMemo(() => {
    const { pageBase, geos, regions, shards, ...rest }: sloGroupSearchObjectType = qs.parse(search);
    const cohorts: string =
      (geos ? `&geos=${geos}` : '') +
      (regions ? `&regions=${regions}` : '') +
      (shards ? `&shards=${shards}` : '');
    return {
      ...rest,
      currentPageBase: parseInt(pageBase || '1', 10),
      searchWithoutPage: qs.stringify(rest),
      cohortParams: cohorts,
    };
  }, [search]);

  const handleChangePage = useCallback((e: any, page: number) => {
    if (page > 1) {
      setQuery({
        pageBase: page,
      });
    } else {
      setQuery({
        pageBase: undefined,
      });
    }
    onPageChange(e, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sloGroupCohortSummaries: SloGroupCohortSummaryDTO[] =
    sloGroupData?.sloGroupSummary?.sloGroupCohortSummaries || [];
  const hasCohorts: boolean = sloGroupCohortSummaries.length > 0;
  // Currently, to infer rollup and base cohorts, we are using the following logic:
  // 1. If there is DB cohort, it is the base cohort.
  // 2. If there is no DB cohort, but there is a SHARD cohort, it is the base cohort.
  // 3. If there is no DB or SHARD cohort, but there is REGION cohort, REGION is the base cohort.
  // 4. If there is no DB, SHARD or REGION cohort, there is no base cohort.
  const baseCohort =
    sloGroupCohortSummaries.find(x => x.cohort === CohortDTOTypeEnum.Db) ||
    sloGroupCohortSummaries.find(x => x.cohort === CohortDTOTypeEnum.Shard) ||
    sloGroupCohortSummaries.find(x => x.cohort === CohortDTOTypeEnum.Region) ||
    null;

  // Rollup SLOs
  const { queryKey: queryKeyRollup, path: pathRollup, cohorts: cohortsRollup } = useMemo(() => {
    const cohortsFiltered: SloGroupCohortSummaryDTO[] = sloGroupCohortSummaries
      .filter(cohort => (baseCohort && baseCohort.cohort !== cohort.cohort) || !baseCohort)
      // We do not filter out draft from query fetching, but from the cohorts list
      .filter(cohort => (hideDrafts && !cohort.draft) || !hideDrafts);
    const params: string = cohortsFiltered.reduce(
      (acc, cohort) => `${acc}&levels=${cohort.cohort}`,
      // TODO: currently API throws error when passing sort param:
      // e.g name, hasBreachStatus, hasQualityIssues
      `${mapParamsToAPIQuery({} as Params, false)}&graph=spark&size=2000${
        hasBreachStatusOnly ? '&hasBreachStatusOnly=true' : ''
      }${cohortParams}`
    );

    return {
      ...querySpec('slo-group.{id}.slos.{params}.{page}', {
        id: publicId,
        param: params,
        page: '1', // no need to use pagination for rollup slos
      }),
      cohorts: cohortsFiltered,
    };
  }, [
    publicId,
    hasBreachStatusOnly,
    hideDrafts,
    baseCohort,
    sloGroupCohortSummaries,
    cohortParams,
  ]);
  // Not to load rollup query if Base filter is selected or no cohorts are available
  const dismissRollup: boolean = cohortType === CohortType.Base || cohortsRollup.length === 0;
  const { data: dataRollup, isInitialLoading: isLoadingRollup } = useQuery(
    queryKeyRollup,
    () => apiFetch(pathRollup),
    {
      onError,
      enabled: !dismissRollup && hasCohorts,
    }
  );

  const rollupSLOsGrouped = useMemo(
    () => groupBy(dataRollup?.content || [], 'indicator.groupType'),
    [dataRollup]
  );

  // Base(Shard) SLOs
  const { queryKey: queryKeyBase, path: pathBase, cohorts: cohortsBase } = useMemo(() => {
    // We do not filter out draft from query fetching, but from the cohorts list
    const cohortsFiltered: SloGroupCohortSummaryDTO[] = (baseCohort ? [baseCohort] : []).filter(
      cohort => (hideDrafts && !cohort.draft) || !hideDrafts
    );
    const param: string = cohortsFiltered.reduce(
      (acc, cohort) => `${acc}&levels=${cohort.cohort}`,
      `${mapParamsToAPIQuery({} as Params, false)}&size=${BASE_SLOS_PAGE_SIZE}${
        hasBreachStatusOnly ? '&hasBreachStatusOnly=true' : ''
      }${cohortParams}`
    );

    return {
      ...querySpec('slo-group.{id}.slos.{params}.{page}', {
        id: publicId,
        param,
        page: currentPageBase.toString(),
      }),
      cohorts: cohortsFiltered,
    };
  }, [publicId, hasBreachStatusOnly, hideDrafts, currentPageBase, baseCohort, cohortParams]);
  // Not to load rollup query if Base filter is selected or no cohorts are available
  const dismissBase: boolean = cohortType === CohortType.Rollup || cohortsBase.length === 0;

  const { data: dataBase, isInitialLoading: isLoadingBase } = useQuery(
    queryKeyBase,
    () => apiFetch(pathBase),
    {
      onError,
      enabled: !dismissBase && hasCohorts,
    }
  );

  const { baseSLOsGrouped, baseLabel } = useMemo(() => {
    const groups = groupBy(dataBase?.content || [], 'indicator.groupType');
    const keys = Object.keys(groups);
    const label: string = keys.length > 1 || keys.length === 0 ? 'base' : keys[0];
    return {
      baseSLOsGrouped: groups,
      baseLabel: label,
    };
  }, [dataBase]);
  const pages: number[] = useMemo(() => getPagesFromTotalPage(dataBase?.totalPages), [dataBase]);
  const showPagination: boolean = (dataBase?.totalPages ?? 0) > 1;

  useEffect(() => {
    // This ONLY happens when user navigates to a url with pageBase param. In
    // this case, only update the local currentPage state
    if (currentPageBase !== currentPage) {
      setCurrentPage(currentPageBase);
      return;
    }

    // This happens when user chooses any type of filter except pagination. In
    // this case, reset the local page state to 1 and update the url (by simply
    // calling our handle function).
    if (currentPageBase > 1) {
      handleChangePage(null, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWithoutPage]);

  if (!hasCohorts) {
    return (
      <EmptyState
        header=""
        description="No cohorts or SLOs found for this SLO group"
        imageUrl="/static/not-found.svg"
      />
    );
  }

  return (
    <div css={extendWidth}>
      <div css={isFullScreen ? stickyHeadStyles : ''}>
        {isFullScreen && (
          <div css={[breakWord]}>
            <h4 css={[flexStart, spaceGapMinimum]}>
              <ChildIssuesIcon label="SLO Group Icon" size="small" />
              {name}
            </h4>
          </div>
        )}
        <FilterBar
          publicId={publicId}
          baseLabel={baseLabel.toLowerCase()}
          sloGroupCohortSummaries={sloGroupCohortSummaries}
        />
      </div>
      <div css={[spaceTopCozy, flexDirectionColumn, flex, spaceGapRelaxed]}>
        {isLoadingRollup && <SearchPageSkeleton isCompact />}
        {!isLoadingRollup &&
          !dismissRollup &&
          cohortTypeSorted.map((type: CohortDTOTypeEnum) => {
            const slos = rollupSLOsGrouped[type];
            if (!slos) return null;
            const isDraft = !!sloGroupCohortSummaries.find(
              (x: SloGroupCohortSummaryDTO) => x.cohort === type
            )?.draft;
            return <SLOCohort key={type} isRollup slos={slos} cohort={type} isDraft={isDraft} />;
          })}
        <div css={[dismissBase ? '' : baseSloCohortPlaceholderStyles]}>
          {isLoadingBase && <SearchPageSkeleton isCompact />}
          {!isLoadingBase &&
            !dismissBase &&
            cohortTypeSorted.map((type: CohortDTOTypeEnum) => {
              const slos = baseSLOsGrouped[type];
              if (!slos) return null;
              const isDraft = !!sloGroupCohortSummaries.find(
                (x: SloGroupCohortSummaryDTO) => x.cohort === type
              )?.draft;
              return (
                <SLOCohort
                  key={type}
                  isRollup={false}
                  slos={slos}
                  cohort={type}
                  isDraft={isDraft}
                  showPagination={showPagination}
                  pages={pages}
                  currentPage={currentPageBase}
                  onPageChange={handleChangePage}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default SLOPanel;
